import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";
import type { Customer } from "@/features/customers/services/customersApi";
import type { Order } from "./ordersApi";

/**
 * Walk-in fast-path payloads (#24, #29). The backend creates the customer (or reuses an
 * existing one matched by phone), creates the order in `WalkInSale` status, decrements
 * stock, creates a payment, optionally consumes an M-Pesa receipt, and creates a
 * `Dispatched` dispatch — all in one transaction.
 */
export interface WalkInOrderItemPayload {
  varietyPricingId: string;
  greenhouseStockEntryId: string;
  /** Expo source line identifier for Expo-only walk-in sales. */
  expoStockEntryId?: string | null;
  /** Always in the stock's native unit (trays for tray entries, pieces for per-seedling entries). */
  quantity: number;
  /** Tray type (210/442) — backend infers from VarietyPricing if omitted. */
  trayType?: number | null;
  /**
   * Set when the cashier sells per-seedling from a tray stock batch.
   * The backend prices on this count (seedlings × pricePerSeedling) and stores
   * the tray-fraction (seedlingQuantity ÷ physical tray hole count) for dispatch tracking.
   */
  seedlingQuantity?: number | null;
}

/** One tender line in a split-payment walk-in (e.g. Cash 6,000 + Mpesa 4,000 = 10,000 cart). */
export interface WalkInPaymentLinePayload {
  /** FK to a registered, approved PaymentMethodAccount (Cash, MpesaTill, MpesaPaybill, Bank). */
  paymentMethodAccountId: string;
  /** Amount tendered against this account — must be > 0. */
  amount: number;
  /** Required when the resolved account is an Mpesa type; ignored otherwise. */
  mpesaReceiptNumber?: string | null;
}

export interface CreateWalkInOrderPayload {
  specialDiscountRequest?: { kind: "Percentage" | "FixedAmount"; value: number; reason: string } | null;
  /** Set when the cashier picked an existing customer from the lookup result. */
  existingCustomerId?: string | null;
  /** Required — used for de-dup match and customer record. Backend normalises the format. */
  phone: string;
  /** Required when `existingCustomerId` is null. Other fields below are optional but accepted. */
  newCustomerName?: string;
  email?: string | null;
  idNumber?: string | null;
  county?: string | null;
  town?: string | null;
  road?: string | null;
  physicalLocationName?: string | null;
  gpsLocation?: string | null;
  items: WalkInOrderItemPayload[];
  /**
   * One or more tender lines whose amounts sum to the cart total (walk-ins cannot carry
   * a balance). A single Cash line is the common case; multiple lines support split tender.
   */
  payments: WalkInPaymentLinePayload[];
  /** Optional link to a configured transport rate; server computes cost as rate x tray quantity. */
  transportRateId?: string | null;
  /** Optional manual transport cost. When provided, server ignores transportRateId. */
  manualTransportCost?: number | null;
  notes?: string | null;
}

/** Resolved effective discount for a customer (mirrors backend EffectiveDiscountDto). */
export interface EffectiveDiscount {
  customerId: string;
  customerName: string;
  effectiveDiscountPercentage: number;
  /** "Individual" | "GroupCampaign" | "GlobalCampaign" | "GroupDefault" | "None". */
  discountSource: string;
  discountId?: string | null;
  discountDetails?: string | null;
  /** Non-blocking note, e.g. a group + global campaign are both active. */
  warning?: string | null;
  candidates?: Array<{ source: string; discountId: string | null; name: string; percentage: number;
    validFrom: string | null; validUntil: string | null; eligible: boolean; selected: boolean; explanation: string }>;
  precedenceExplanation?: string | null;
}

export const walkInOrdersApi = {
  /** Phone-based customer lookup. Returns null when no match. */
  lookupByPhone: async (phone: string): Promise<Customer | null> => {
    const { data } = await apiClient.post<ApiResponse<Customer | null>>(
      "/Orders/walk-in/lookup",
      { phone }
    );
    return data.data ?? null;
  },

  /**
   * Effective discount preview for a customer. Used by both the Create Order dialog and the
   * walk-in flow so the discount is visible before submit. Gated by orders.view on the backend.
   */
  getEffectiveDiscount: async (customerId: string): Promise<EffectiveDiscount> => {
    const { data } = await apiClient.get<ApiResponse<EffectiveDiscount>>(
      `/Orders/effective-discount/${customerId}`
    );
    return data.data;
  },

  createWalkInOrder: async (payload: CreateWalkInOrderPayload): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      "/Orders/walk-in",
      payload
    );
    return data.data;
  },
};
